import type { z } from 'zod';

import {
  indexSearchSchema,
  listParams,
  type FilterOption,
  type ListParams,
} from '@nucleus-commerce/admin-kit/index-table';
import { SocialPostStatus, type SocialProvider } from '@nucleus-commerce/api-client/admin';

import { LIVE_STATUSES } from './post-state';

const valuesOf = (values: Record<string, string>): FilterOption[] =>
  Object.values(values).map((value) => ({ value, label: value }));

/**
 * The filters as the URL and GET /social/posts spell them (`status` is a comma list). The network
 * is not one of them: each network's section lists its own posts (`scopedPostParams`).
 */
export const POST_FILTER_FIELDS = [
  { key: 'status', type: 'enum', options: valuesOf(SocialPostStatus) },
] as const;

/** URL search params of the posts list; the endpoint has no sort (newest first). */
export const postListSearchSchema = indexSearchSchema(POST_FILTER_FIELDS);
export type PostListSearch = z.infer<typeof postListSearchSchema>;

/**
 * Without a status filter the list leaves `removed` posts out; the endpoint itself answers every
 * status. The route loader and the page's `useSearchListState().params` both pass through here so
 * the prefetched query and the page's query share one key.
 */
export function withDefaultStatus(params: ListParams): ListParams {
  return params.status ? params : { ...params, status: LIVE_STATUSES.join(',') };
}

/** One network's list: the default status filter plus `provider`. */
export function scopedPostParams(params: ListParams, provider: SocialProvider): ListParams {
  return { ...withDefaultStatus(params), provider };
}

/** The list request for a validated search in one network's section. */
export function postListParams(search: PostListSearch, provider: SocialProvider): ListParams {
  return scopedPostParams(listParams(search, POST_FILTER_FIELDS), provider);
}
